import { createContext, useContext, useEffect, useRef, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const LAST_ACTIVITY_KEY = "giftmap_last_activity";
const CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

interface InactivityContextType {
  recordActivity: () => void;
}

const InactivityContext = createContext<InactivityContextType | null>(null);

export const InactivityProvider = ({ children }: { children: ReactNode }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const recordActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  };

  const checkInactivity = async () => {
    if (!user?.id) return;

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) {
      recordActivity();
      return;
    }

    const elapsed = Date.now() - parseInt(lastActivity);
    if (elapsed > INACTIVITY_TIMEOUT_MS) {
      console.log("Inactivity timeout reached - signing out");
      await signOut();
      navigate("/auth");
    }
  };

  // Record activity on user interactions
  useEffect(() => {
    if (!user?.id) return;

    const handleActivity = () => recordActivity();

    // Listen to various user interactions
    window.addEventListener("touchstart", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);

    // Initial activity record
    recordActivity();

    return () => {
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, [user?.id]);

  // Periodic inactivity check while app is in foreground
  useEffect(() => {
    if (!user?.id) return;

    checkIntervalRef.current = setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [user?.id]);

  // Handle app state changes (background/foreground) - native only
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user?.id) return;

    const handleAppStateChange = async ({ isActive }: { isActive: boolean }) => {
      if (isActive) {
        // App returned to foreground - check inactivity
        await checkInactivity();
      }
    };

    App.addListener("appStateChange", handleAppStateChange);

    return () => {
      App.removeAllListeners();
    };
  }, [user?.id, signOut, navigate]);

  return (
    <InactivityContext.Provider value={{ recordActivity }}>
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivityContext = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error("useInactivityContext must be used within InactivityProvider");
  }
  return context;
};
